import { useMemo } from 'react';

/**
 * Grid Snap Algorithm
 *
 * Takes raw room polygons (vertices in 0-1 normalized coords from Vision)
 * and aligns nearby edges, forces orthogonal angles, extracts wall segments.
 *
 * Pipeline:
 * 1. COLLECT  — gather all X and Y coordinates from all rooms
 * 2. CLUSTER  — group nearby values within tolerance
 * 3. SNAP     — replace each coord with cluster median
 * 4. WALLS    — extract wall segments from polygon edges
 * 5. SCALE    — convert 0-1 → virtual coords preserving aspect ratio
 */

const SNAP_TOLERANCE = 0.04;     // 4% of image — edges closer than this are aligned
                                  // Larger tolerance needed because Vision model returns
                                  // coordinates with ~2-3% error between adjacent rooms
const MIN_WALL_LENGTH = 0.015;   // Walls shorter than 1.5% are discarded

// ── Clustering ──

function clusterValues(values, tolerance) {
  if (values.length === 0) return [];
  const sorted = [...values].sort((a, b) => a - b);
  const clusters = [];
  let current = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] <= tolerance) {
      current.push(sorted[i]);
    } else {
      clusters.push(current);
      current = [sorted[i]];
    }
  }
  clusters.push(current);

  // Each cluster → { median, members }
  return clusters.map(c => ({
    median: c[Math.floor(c.length / 2)],
    min: c[0],
    max: c[c.length - 1],
    count: c.length,
  }));
}

function snapToCluster(val, clusters) {
  for (const cl of clusters) {
    if (val >= cl.min - SNAP_TOLERANCE && val <= cl.max + SNAP_TOLERANCE) {
      return cl.median;
    }
  }
  return val; // no cluster found
}

// ── Wall extraction ──

function extractWalls(rooms) {
  const wallSet = new Map(); // key → wall object
  const wallKey = (x1, y1, x2, y2) => {
    // Normalize direction so (A→B) and (B→A) have same key
    if (x1 < x2 || (x1 === x2 && y1 < y2)) return `${x1},${y1}-${x2},${y2}`;
    return `${x2},${y2}-${x1},${y1}`;
  };

  rooms.forEach((room, rIdx) => {
    const verts = room.vertices;
    if (!verts || verts.length < 3) return;

    for (let i = 0; i < verts.length; i++) {
      const [x1, y1] = verts[i];
      const [x2, y2] = verts[(i + 1) % verts.length];

      // Skip tiny walls
      const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
      if (len < MIN_WALL_LENGTH) continue;

      const key = wallKey(x1, y1, x2, y2);
      if (wallSet.has(key)) {
        // Shared by 2 rooms → internal wall
        wallSet.get(key).type = 'internal';
        wallSet.get(key).rooms.push(rIdx);
      } else {
        wallSet.set(key, {
          from: [x1, y1],
          to: [x2, y2],
          type: 'external', // will be upgraded to internal if shared
          rooms: [rIdx],
          length: len,
        });
      }
    }
  });

  return [...wallSet.values()];
}

// ── Main hook ──

export default function useGridSnap(rawGeometryRooms, virtualW, virtualH) {
  return useMemo(() => {
    if (!rawGeometryRooms || rawGeometryRooms.length === 0) {
      return { snappedRooms: [], walls: [] };
    }

    // 1. Collect all coordinates
    const allX = [];
    const allY = [];
    rawGeometryRooms.forEach(r => {
      if (!r.vertices) return;
      r.vertices.forEach(([x, y]) => {
        allX.push(x);
        allY.push(y);
      });
    });

    // ── NO REMAP — Vision (via grid overlay) retorna coordenadas já ALINHADAS
    // com a imagem de fundo. Qualquer remap/esticamento desalinha os cômodos
    // em relação à planta renderizada atrás. Mantemos as coords originais.
    //
    // O anchor abaixo vira identidade (rangeX=1, PAD=0) só pra não quebrar
    // o código do parent que ainda consome o objeto anchor.
    const minXRaw = 0;
    const minYRaw = 0;
    const rangeX = 1;
    const rangeY = 1;

    // 2. Cluster nearby values (coords originais)
    const clustersX = clusterValues(allX, SNAP_TOLERANCE);
    const clustersY = clusterValues(allY, SNAP_TOLERANCE);

    // 3. Snap all vertices to clusters
    const snappedNorm = rawGeometryRooms.map(r => {
      if (!r.vertices) return r;
      return {
        ...r,
        vertices: r.vertices.map(([x, y]) => [
          snapToCluster(x, clustersX),
          snapToCluster(y, clustersY),
        ]),
      };
    });

    // 4. Extract walls from snapped polygons
    const wallsNorm = extractWalls(snappedNorm);

    // 5. Scale to virtual coordinates
    const snappedRooms = snappedNorm.map(r => {
      if (!r.vertices) return r;
      const scaledVerts = r.vertices.map(([x, y]) => [x * virtualW, y * virtualH]);

      // Compute bounding box from vertices
      const xs = scaledVerts.map(v => v[0]);
      const ys = scaledVerts.map(v => v[1]);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);

      return {
        ...r,
        vertices: scaledVerts,
        // Also provide ax/ay/aw/ah for backwards compatibility
        ax: minX,
        ay: minY,
        aw: Math.max(...xs) - minX,
        ah: Math.max(...ys) - minY,
      };
    });

    const walls = wallsNorm.map((w, idx) => ({
      id: `wall-${idx}`,
      x1: w.from[0] * virtualW,
      y1: w.from[1] * virtualH,
      x2: w.to[0] * virtualW,
      y2: w.to[1] * virtualH,
      type: w.type,
      rooms: w.rooms,
      dir: Math.abs(w.from[0] - w.to[0]) < 0.01 ? 'v' : 'h',
    }));

    // Anchor info — identidade (sem remap). Mantido pra compatibilidade com o parent.
    const anchor = {
      minX: minXRaw,
      minY: minYRaw,
      rangeX,
      rangeY,
      PAD: 0,
    };

    return { snappedRooms, walls, anchor };
  }, [rawGeometryRooms, virtualW, virtualH]);
}
